import * as React from "react";
import Grid from "@mui/material/Grid";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MediaCard, { sortBanners } from "./Common/Mediacard";
import {
  postBanner,
  getBanners,
  putUpdateBanner,
  putReorderBanners,
  deleteBannerById,
} from "../Utils/vendor-customer-service";
import { BANNER_DISPLAY } from "../Utils/constant";

const emptyBannerForm = {
  imageUrl: "",
  link: "",
};

function BannerManager({ bannerType, title, description }) {
  const display = BANNER_DISPLAY[bannerType] || BANNER_DISPLAY.PRIMARY;
  const [bannerObj, setBannerobj] = React.useState(emptyBannerForm);
  const [editingId, setEditingId] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [savingOrder, setSavingOrder] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState(null);

  const [banners, setBanners] = React.useState([]);

  const fetchBanners = React.useCallback(() => {
    setLoading(true);
    getBanners(bannerType)
      .then((response) => {
        if (response?.status === 200) {
          setBanners(sortBanners(response?.data?.rows || []));
          setBannerobj(emptyBannerForm);
          setEditingId(null);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [bannerType]);

  React.useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const resetForm = () => {
    setBannerobj(emptyBannerForm);
    setEditingId(null);
  };

  const saveBanner = (event) => {
    event?.preventDefault();
    if (!bannerObj?.imageUrl) {
      return;
    }

    const request = editingId
      ? putUpdateBanner({ ...bannerObj, id: editingId })
      : postBanner({ ...bannerObj, type: bannerType });

    request
      .then((response) => {
        if (response?.status === 200 || response?.status === 204) {
          fetchBanners();
        }
      })
      .catch(() => {});
  };

  const editBanner = (banner) => {
    setEditingId(banner.id);
    setBannerobj({
      imageUrl: banner.imageUrl || "",
      link: banner.link || "",
    });
  };

  const deleteBanner = (id) => {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(`Are you sure you want to delete this banner?
      This action cannot be undone.`)
    ) {
      deleteBannerById({ id })
        .then((response) => {
          if (response?.status === 204) {
            fetchBanners();
          }
        })
        .catch(() => {});
    }
  };

  const toggleBannerActive = (banner) => {
    const nextActive = !banner.isActive;
    setTogglingId(banner.id);
    setBanners((prev) =>
      prev.map((item) =>
        item.id === banner.id ? { ...item, isActive: nextActive } : item,
      ),
    );

    putUpdateBanner({ id: banner.id, isActive: nextActive })
      .then((response) => {
        if (response?.status !== 204) {
          fetchBanners();
        }
      })
      .catch(() => {
        fetchBanners();
      })
      .finally(() => {
        setTogglingId(null);
      });
  };

  const handleReorder = (reorderedBanners) => {
    setBanners(reorderedBanners);
    setSavingOrder(true);

    putReorderBanners({
      type: bannerType,
      banners: reorderedBanners.map((banner, index) => ({
        id: banner.id,
        order: index + 1,
      })),
    })
      .then((response) => {
        if (response?.status !== 204) {
          fetchBanners();
        }
      })
      .catch(() => {
        fetchBanners();
      })
      .finally(() => {
        setSavingOrder(false);
      });
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography gutterBottom variant="h5" component="div">
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            name="imageUrl"
            required
            fullWidth
            id={`${bannerType}-imageUrl`}
            label="Image Link"
            value={bannerObj?.imageUrl}
            onChange={(e) =>
              setBannerobj((prevData) => ({
                ...prevData,
                imageUrl: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            name="link"
            fullWidth
            id={`${bannerType}-link`}
            label="Click Link"
            value={bannerObj?.link}
            onChange={(e) =>
              setBannerobj((prevData) => ({
                ...prevData,
                link: e.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              Recommended resolution:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {display.resolutionLabel}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Button onClick={saveBanner} variant="contained" sx={{ mr: 1 }}>
            {editingId ? "UPDATE BANNER" : "ADD BANNER"}
          </Button>
          {editingId ? (
            <Button onClick={resetForm} variant="outlined">
              Cancel
            </Button>
          ) : null}
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="h6" component="div">
              Banners ({banners?.length})
            </Typography>
            {savingOrder ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Saving order...
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Drag the handle to reorder
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <MediaCard
              banners={banners}
              onReorder={handleReorder}
              editBanner={editBanner}
              deleteBanner={deleteBanner}
              toggleBannerActive={toggleBannerActive}
              editingId={editingId}
              togglingId={togglingId}
              cardWidth={display.cardWidth}
              aspectRatio={display.aspectRatio}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}

export default BannerManager;
